import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';

import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import {
  Ipv6Group,
  Ipv6GroupPagingRes,
} from '@/shared/interfaces/erp/system-settings';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchIpv6Groups = (
  params?: GetIpv6GroupsParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['ipv6Groups', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getIpv6Groups(params);
    },
    enabled,
  });
};

export const useGetIpv6Group = (id: string) => {
  return useQuery({
    queryKey: ['ipv6Groups', id],
    queryFn: async () => {
      return getIpv6Group(id);
    },
  });
};

export const useCreateIpv6Group = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: createIpv6Group,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv6Groups'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Grupo IPv6 creado con éxito',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateIpv6Group = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateIpv6Group,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv6Groups'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Grupo IPv4 actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveIpv6Group = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeIpv6Group,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv6Groups'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetIpv6GroupsParams = Partial<Ipv6Group> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateIpv6GroupParams = Omit<Ipv6Group, 'id_grupo_ipv6'>;

export type UpdateIpv6GroupParams = {
  id: string;
  data: CreateIpv6GroupParams;
};

export const getIpv6Groups = (params?: GetIpv6GroupsParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<Ipv6GroupPagingRes>(
    `${urlBackend}api/v1/grupo-ipv-6/?${queryParams}`,
    true
  );
};

export const getIpv6Group = async (id: string) => {
  try {
    return await get<Ipv6Group>(`${urlBackend}api/v1/grupo-ipv-6/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createIpv6Group = async (data: CreateIpv6GroupParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Ipv6Group>(`${urlBackend}api/v1/grupo-ipv-6/`, data, true);
};

export const updateIpv6Group = async ({ id, data }: UpdateIpv6GroupParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Ipv6Group>(`${urlBackend}api/v1/grupo-ipv-6/${id}/`, data, true);
};

export const removeIpv6Group = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Ipv6Group>(
    `${urlBackend}api/v1/grupo-ipv-6/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
